import * as React from 'react';
import * as _ from 'underscore';
import * as $ from 'jquery';

import { IGlob } from '../../ts/Glob';
import * as permissions from '../../../../_common/platform/ts/permissions';
import MenuItem, { ButtonMenuItem, Divider } from './MenuItem';
import { IValuesCollection } from '../../../../_common/platform/ts/appTheme';
import { WorkspaceLayoutType } from '../../../../pdc-platform/src/ts/Interfaces';
import { GuideButton, UserProfileButton } from './ToolbarButtons';
import { DisplayMode } from '../../ts/stores/WorkspaceStore';
import { Remainders } from '../notifications/Remainders';

const COMMAND_TITLE_OPTIONS = 'Workspace Options';
const COMMAND_TITLE_SAVE_WORKSPACE = 'Save Workspace';
const COMMAND_TITLE_SAVE_WORSPACE_AS = 'Save Workspace As';
const COMMAND_TITLE_SAVE_AS = 'Save As';
const COMMAND_TITLE_WORKSPACE_CREATE_LINK = 'Create Workspace Link';
const COMMAND_TITLE_REFRESH_WORKSPACE = 'Refresh';

export interface ICommandMenuProperties {
    displayMode: DisplayMode;
    showGrid: boolean;
    theme: IValuesCollection;
    layoutType: WorkspaceLayoutType;
    commandButtonStyle: React.CSSProperties;
    workspaceIsNew: boolean;
    workspaceCanBeSaved: boolean;
    workspaceIsNotSaved: boolean;
    showWidgetTemplates: boolean;
    editingActiveButtons: React.JSX.Element;
    glob: IGlob;

    onEditWorkspace(): void;
    onShowGrid(): void;
    onEditTheme(): void;
    onLayoutReset(): void;
    onSave(): void;
    onSaveAs(): void;
    onResetChanges(): void;
    onCreateWorkspaceLink(): void;
    onRefreshWorkspace(): void;
}

function ActiveButton(props: { icon: string; title?: string; active?: boolean; onClick(e): void }) {
    return (
        <div className="active-button">
            <button
                type="button"
                className={'btn btn-default' + (props.active ? ' active' : '')}
                onClick={props.onClick}
            >
                <span className={props.icon} />
                {props.title && <span> {props.title} </span>}
            </button>
        </div>
    );
}

function EditingMenuItems(props: {
    editMode: boolean;
    layoutType: WorkspaceLayoutType;
    showGrid: boolean;
    workspaceCanBeSaved: boolean;
    workspaceIsNew: boolean;
    onEditWorkspace(): void;
    onShowGrid(): void;
    onEditTheme(): void;
    onLayoutReset(): void;
    onSave(): void;
    onSaveAs(): void;
}) {
    const result: React.JSX.Element[] = [];

    if (props.layoutType === 'normal') {
        result.push(
            <ButtonMenuItem
                key="edit-ws"
                icon={'glyphicon glyphicon-wrench'}
                isActive={props.editMode}
                title={'Edit Workspace'}
                onClick={props.onEditWorkspace}
            />
        );

        result.push(
            <ButtonMenuItem
                key="edit-grid"
                icon={'glyphicon glyphicon-th'}
                isActive={props.showGrid}
                title={'Edit Grid'}
                onClick={props.onShowGrid}
            />
        );
    }

    if (props.showGrid) {
        result.push(
            <ButtonMenuItem
                key="reset-grid"
                icon="glyphicon glyphicon-repeat"
                title={'Reset Grid Layout'}
                onClick={props.onLayoutReset}
            />
        );
    }

    if (props.layoutType !== 'narrow') {
        result.push(
            <ButtonMenuItem
                key="edit-ws-theme"
                icon="glyphicon glyphicon-pencil"
                title={COMMAND_TITLE_OPTIONS}
                onClick={props.onEditTheme}
            />
        );
    }

    if (props.workspaceCanBeSaved) {
        result.push(<Divider key="divider-saving" />);

        if (!props.workspaceIsNew) {
            result.push(
                <ButtonMenuItem
                    key="ws-save"
                    icon="glyphicon glyphicon-floppy-disk"
                    title={COMMAND_TITLE_SAVE_WORKSPACE}
                    onClick={props.onSave}
                />
            );
        }

        result.push(
            <ButtonMenuItem
                key="ws-save-as"
                icon="glyphicon glyphicon-floppy-save"
                title={COMMAND_TITLE_SAVE_WORSPACE_AS}
                onClick={props.onSaveAs}
            />
        );
    }

    return result;
}

export function EditingActiveButtons(props: {
    displayMode: DisplayMode;
    showGrid: boolean;
    workspaceIsNew: boolean;
    workspaceCanBeSaved: boolean;
    workspaceIsNotSaved: boolean;
    showWidgetTemplates: boolean;
    onEditWorkspace(): void;
    onShowGrid(): void;
    onSave(): void;
    onSaveAs(): void;
    onResetChanges(): void;
    onShowWidgetTemplates(show: boolean): void;
}) {
    const result: React.JSX.Element[] = [];

    if (!props.showGrid && props.displayMode !== 'main') {
        result.push(
            <ActiveButton
                key="show-workspace-template-sidebar"
                title={'Widget Templates'}
                icon={'glyphicon glyphicon-wrench'}
                active={props.showWidgetTemplates}
                onClick={() => {
                    if (props.onShowWidgetTemplates != null) {
                        props.onShowWidgetTemplates(!props.showWidgetTemplates);
                    }
                }}
            />
        );
    }

    if (props.displayMode === 'widgets') {
        result.push(
            <ActiveButton
                key="stop-editing"
                title={'Stop Editing Workspace'}
                icon={'glyphicon glyphicon-stop'}
                onClick={props.onEditWorkspace}
            />
        );
    } else if (props.workspaceIsNew) {
        result.push(
            <ActiveButton
                key="start-editing"
                title={'Edit Workspace'}
                icon={'glyphicon glyphicon-wrench'}
                onClick={props.onEditWorkspace}
            />
        );
    }

    if (props.showGrid) {
        result.push(
            <ActiveButton
                key="edit-grid"
                title={'Stop Editing Grid'}
                icon={'glyphicon glyphicon-stop'}
                onClick={props.onShowGrid}
            />
        );
    }

    if (props.workspaceCanBeSaved && props.workspaceIsNotSaved) {
        if (!props.workspaceIsNew) {
            result.push(
                <ActiveButton
                    key="ws-save"
                    title={COMMAND_TITLE_SAVE_WORKSPACE}
                    icon={'glyphicon glyphicon-floppy-disk'}
                    onClick={props.onSave}
                />
            );
        }

        result.push(
            <ActiveButton
                key="ws-save-as"
                title={COMMAND_TITLE_SAVE_AS}
                icon={'glyphicon glyphicon-floppy-save'}
                onClick={props.onSaveAs}
            />
        );

        if (!props.workspaceIsNew) {
            result.push(
                <ActiveButton
                    key="ws-drop-changes"
                    title={'Drop Changes'}
                    icon="glyphicon glyphicon-repeat"
                    onClick={props.onResetChanges}
                />
            );
        }
    }

    return result;
}

export function CommandMenu(props: ICommandMenuProperties) {
    const [showMenu, setShowMenu] = React.useState(false);

    React.useEffect(() => {
        const handleContextMenuHide = (e) => {
            if (e.target.className === 'form-control' || e.target.className === 'glyphicon glyphicon-option-vertical') {
                return;
            }

            setShowMenu(false);
        };

        $(document.body).on('click', handleContextMenuHide);

        return () => {
            $(document.body).off('click', handleContextMenuHide);
        };
    }, []);

    const handleOnComandsMenuClick = (e) => {
        setShowMenu(!showMenu);
    };

    const handleOnLogoutClick = (e) => {
        props.glob.DialogActions.createDialog(
            'Logout from system',
            'You are about to logout from system.',
            () => {
                props.glob.AppActions.signOut();
                props.glob.mainRouter.navigate('/', {
                    replace: true,
                    trigger: false
                });
            },
            () => {},
            () => {},
            'confirm',
            {
                cancelTitle: null
            }
        );

        e.stopPropagation();
        e.preventDefault();
    };

    // Hide unless we will find PDF exporter better than PhantomJS
    // OE: 21/01/2020
    // workspaceIsPrintable() {
    //     const themeOption = props.theme['workspace-printable'];
    //     return themeOption != null ? themeOption : true;
    // }

    const edittingIsAllowed = permissions.canEdit();
    const narrowLayoutType = props.layoutType === 'narrow';
    const activeButtons = [];

    if (!narrowLayoutType) {
        if (edittingIsAllowed && props.editingActiveButtons != null) {
            activeButtons.push(props.editingActiveButtons);
        }

        activeButtons.push(
            <UserProfileButton key="user-profile-button" buttonClass={'command-button'} glob={props.glob} />
        );

        activeButtons.push(<GuideButton key="Guide-button" buttonClass={ 'command-button' } glob={ props.glob } />);
        
        activeButtons.push(<Remainders key="Remainders-button" glob={ props.glob } />);
    }

    // let exportMenu;

    // if (workspaceIsPrintable()) {
    //     exportMenu = [
    //         <Divider key="divider-export" />
    //         ,
    //         <ExportMenu
    //             key={ 'export-menu' }
    //             glob={ props.glob }
    //         />
    //     ]
    // }

    const actionMenuItems: React.JSX.Element[] = [];

    if (!props.workspaceIsNew) {
        actionMenuItems.push(
            <ButtonMenuItem
                key="ws-reset"
                icon="glyphicon glyphicon-repeat"
                title={'Reload Workspace'}
                onClick={props.onResetChanges}
            />
        );
    }

    if (edittingIsAllowed) {
        actionMenuItems.push(
            <EditingMenuItems
                key="editing-menu-items"
                editMode={props.displayMode === 'widgets'}
                layoutType={props.layoutType}
                showGrid={props.showGrid}
                workspaceCanBeSaved={props.workspaceCanBeSaved}
                workspaceIsNew={props.workspaceIsNew}
                onEditWorkspace={props.onEditWorkspace}
                onShowGrid={props.onShowGrid}
                onEditTheme={props.onEditTheme}
                onLayoutReset={props.onLayoutReset}
                onSave={props.onSave}
                onSaveAs={props.onSaveAs}
            />
        );
    }

    if (props.workspaceCanBeSaved && permissions.createWorkspaceLink()) {
        actionMenuItems.push(
            <ButtonMenuItem
                key="ws-create-link"
                icon="glyphicon glyphicon-link"
                title={COMMAND_TITLE_WORKSPACE_CREATE_LINK}
                onClick={props.onCreateWorkspaceLink}
            />
        );
    }

    let refrsehMenuItem: React.JSX.Element;

    if (narrowLayoutType) {
        refrsehMenuItem = (
            <ButtonMenuItem
                key="edit-ws-theme"
                icon="glyphicon glyphicon-refresh"
                title={COMMAND_TITLE_REFRESH_WORKSPACE}
                onClick={props.onRefreshWorkspace}
            />
        );
    }

    const cmdButtonStyle = props.commandButtonStyle || {
        boxShadow: 'none'
    };
    const authData = props.glob.AppStore.getAuthenticationData();

    return (
        <div className="d-flex flex-nowrap command-menu">
            <div className="d-flex flex-nowrap active-buttons">{activeButtons}</div>

            <div className={'dropdown-button command-button' + (showMenu ? ' show-dropdown' : '')}>
                <div
                    className={'btn border-0 navbar-color-pattern'}
                    style={cmdButtonStyle}
                    title={'Commands'}
                    onClick={handleOnComandsMenuClick}
                >
                    <span className="glyphicon glyphicon-option-vertical" />
                </div>

                <div className="dropdown-menu">
                    <MenuItem icon="user-icon glyphicon glyphicon-user" top={true}>
                        <span className="user-name-text">{authData.displayName}</span>
                    </MenuItem>

                    <Divider />

                    {actionMenuItems}
                    {refrsehMenuItem}

                    {(actionMenuItems && actionMenuItems.length > 0) || refrsehMenuItem != null ? <Divider /> : null}

                    <ButtonMenuItem
                        icon="glyphicon glyphicon-log-out"
                        title={'Logout'}
                        bottom={true}
                        onClick={handleOnLogoutClick}
                    />
                </div>
            </div>
        </div>
    );
}
